import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AvatarBadge, getAvatarInfo } from '../constants/avatars.jsx';
import { getObjectiveForQuestion } from '../constants/lessons.js';
import { DeckIcon } from './deckIcons.jsx';
import ProjectorQuestion from './ProjectorQuestion.jsx';
import Leaderboard from '../components/Leaderboard.jsx';
import HandTrackingCam from '../components/HandTrackingCam.jsx';
import { RotateCcwIcon } from '../components/Icons.jsx';
import RemovePlayerButton from '../components/RemovePlayerButton.jsx';

/* ------------------------------------------------------------------ Cover */
const ROSTER_CAP = 24;

/** Who has joined so far. Each name can be sent out, so the learner can come back under another. */
export function CoverRoster({ room, preview = false }) {
  const joined = room.players.filter((p) => p.connected);
  const shown = joined.slice(0, ROSTER_CAP);
  const extra = joined.length - shown.length;

  return (
    <div className="stop-cover-roster">
      <div className="stop-cover-roster-head">
        <span className="stop-cover-roster-count num">{joined.length}</span>
        <span className="text-dim">{joined.length > 0 ? 'ผู้เรียนพร้อมแล้ว' : 'รอผู้เรียนสแกน QR เข้าห้อง'}</span>
      </div>
      {joined.length > 0 && (
        <div className="stop-cover-names">
          {shown.map((p) => (
            <span key={p.id} className="stop-cover-name" style={{ '--player-color': getAvatarInfo(p.avatar).bg }}>
              <AvatarBadge avatar={p.avatar} size="sm" />
              <span>{p.nickname}</span>
              {!preview && <RemovePlayerButton player={p} onRemove={room.actions.kickPlayer} className="stop-cover-remove" />}
            </span>
          ))}
          {extra > 0 && <span className="stop-cover-name is-more num">+{extra}</span>}
        </div>
      )}
    </div>
  );
}

/** The QR code and the room code: the way in. */
export function CoverJoin({ room, onOpenQr }) {
  const joinUrl = `${window.location.origin}/join/${room.roomCode}`;
  return (
    <button className="stop-cover-join" onClick={onOpenQr} title="แตะเพื่อขยาย QR">
      <div className="stop-cover-qr">
        <QRCodeSVG value={joinUrl} size={220} bgColor="#ffffff" fgColor="#16203a" level="Q"
          style={{ width: '100%', height: 'auto', display: 'block' }} />
      </div>
      <div className="stop-cover-pin">
        <span className="stop-cover-pin-label">รหัสเข้าเรียน</span>
        <span className="stop-cover-pin-value num">{room.roomCode}</span>
        <span className="stop-cover-pin-alt">แตะเพื่อขยาย · {window.location.host}/join</span>
      </div>
    </button>
  );
}

export function CoverStop({ deck, room, onOpenQr, preview = false }) {
  const meta = deck.meta || { unit: 'บทเรียน', title: 'กิจกรรมในชั้นเรียน' };

  return (
    <section className="stop stop-cover">
      <div className="stop-cover-left">
        <div className="stop-cover-head">
          <span className="stop-kicker">{meta.unit}</span>
          <h1 className="stop-cover-title">{meta.title}</h1>
          {meta.subtitle && <p className="stop-cover-sub">{meta.subtitle}</p>}
          {meta.instructor && <p className="stop-cover-teacher">{meta.instructor}</p>}
        </div>
        <CoverRoster room={room} preview={preview} />
      </div>
      <CoverJoin room={room} onOpenQr={onOpenQr} />
    </section>
  );
}

/* ---------- shared frame: title block at top, content fills the rest ---- */
export function Slide({ kicker, title, badge, badgeWarn, children, className = '' }) {
  return (
    <section className={`stop stop-fill ${className}`}>
      <header className="stop-hd">
        {kicker && <span className="stop-kicker">{kicker}</span>}
        <h1 className="stop-title">
          {title}
          {badge && <span className={`stop-title-badge ${badgeWarn ? 'is-warn' : ''}`}>{badge}</span>}
        </h1>
      </header>
      <div className="stop-content">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------- Objectives */
export function ObjectivesStop({ stop }) {
  return (
    <Slide title={stop.title} className="stop-objectives">
      <div className="stop-obj-grid">
        {stop.items.map((it, i) => (
          <div key={i} className="stop-obj-card">
            <div className="stop-obj-card-head">
              <div className="stop-obj-icon">
                <DeckIcon name={it.icon} size={64} />
              </div>
              <span className="stop-obj-num num">{i + 1}</span>
            </div>
            <h2>{it.head}</h2>
            <p>{it.body}</p>
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* --------------------------------------------------------------- Overview */
export function OverviewStop({ stop }) {
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="stop-overview">
      <div className="stop-steps-row">
        {stop.steps.map((s) => (
          <div key={s.n} className={`stop-step-card ${s.warn ? 'is-warn' : ''}`}>
            <div className="stop-step-card-head">
              <span className="stop-step-badge num">{String(s.n).padStart(2, '0')}</span>
              {s.warn ? (
                <span className="stop-step-warn-pill">สำคัญ</span>
              ) : (
                <span className="stop-step-tag">ขั้นที่ {s.n}</span>
              )}
            </div>
            <div className="stop-step-icon">
              <DeckIcon name={s.icon} size={55} />
            </div>
            <h3>{s.label}</h3>
            <p>{s.note}</p>
            {s.hint && (
              <div className="stop-step-hint">
                <span>{s.hint}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* ------------------------------------------------------------------ Hook */
export function HookStop({ stop }) {
  const [revealed, setRevealed] = React.useState(false);

  return (
    <Slide kicker={stop.kicker} title={stop.title} className="stop-hook">
      <div className="stop-hook-flow">
        <div className="stop-slip-col">
          <figure className="stop-slip-photo">
            <img src={stop.slipImage} alt="สลิปโอนเงินตัวอย่าง" />
          </figure>
          {stop.slipCaption && <p className="stop-slip-caption">{stop.slipCaption}</p>}
        </div>

        <div className="stop-hook-arrow">
          <svg width="56" height="18" viewBox="0 0 44 18" fill="none">
            <path d="M0 9h37m0 0-7-7m7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{stop.arrow}</span>
        </div>

        {!revealed ? (
          <div className="stop-hook-guess">
            <span className="stop-hook-guess-icon"><DeckIcon name="bulb" size={44} /></span>
            <p>{stop.guessPrompt}</p>
            <button type="button" className="btn-fake" onClick={() => setRevealed(true)}>
              ดูคำตอบ
            </button>
          </div>
        ) : (
          <div className="stop-hook-form">
            <div className="stop-hook-form-head">
              <strong>{stop.form.head}</strong>
              {stop.form.note && <span>{stop.form.note}</span>}
            </div>
            <div className="stop-hook-fields">
              {stop.form.fields.map((f, i) => (
                <div key={i} className="stop-hook-field">
                  <span className="stop-hook-field-label">{f.label}</span>
                  <span className="stop-hook-field-value num">{f.value}</span>
                  <span className={`stop-hook-chip is-${f.type}`}>{f.type}</span>
                  <span className="stop-hook-field-note">{f.note}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {revealed && (
        <div className="stop-hook-warn">
          <span className="stop-hook-warn-icon"><DeckIcon name="flame" size={46} /></span>
          <div className="stop-hook-warn-text">
            <strong>{stop.warn.head}</strong>
            <p>{stop.warn.body}</p>
          </div>
          <div className="stop-hook-demo">
            <span className="was num">{stop.warn.before}</span>
            <span className="to">→</span>
            <span className="now num">{stop.warn.after}</span>
            <span className="loss">{stop.warn.loss}</span>
          </div>
        </div>
      )}
    </Slide>
  );
}


/* ------------------------------------------------------------ Bill split */
/* The lesson's opening story, walked one operator at a time: the same bill
   divided three ways answers differently depending on which operator you
   reach for. `steps` on the stop decides how many of the three answers the
   arrow keys have uncovered so far. */
export function BillSplitStop({ stop, substep = 0 }) {
  const shown = stop.results.slice(0, substep);
  const done = substep >= stop.results.length;

  return (
    <Slide kicker={stop.kicker} title={stop.title} className="stop-bill">
      <div className="stop-bill-flow">
        <div className="stop-bill-card">
          <div className="stop-bill-head">
            <strong>{stop.bill.head}</strong>
            {stop.bill.note && <span>{stop.bill.note}</span>}
          </div>
          <div className="stop-bill-total num">
            {stop.bill.total}
            <span className="stop-bill-unit">{stop.bill.unit}</span>
          </div>
          <div className="stop-bill-people">
            {Array.from({ length: stop.bill.people }).map((_, i) => (
              <span key={i} className="stop-bill-person">
                <DeckIcon name="user" size={30} />
              </span>
            ))}
            <span className="stop-bill-people-label">หารกัน {stop.bill.people} คน</span>
          </div>
        </div>

        {/* Nothing but a question mark until the instructor has asked the
            room. The answers then arrive one per press, each one entering on
            its own so the class watches it land instead of reading a list. */}
        <div className="stop-bill-results">
          {shown.length === 0 ? (
            <span className="stop-bill-ask num" aria-hidden="true">?</span>
          ) : (
            shown.map((r, i) => (
              <div
                key={r.op}
                className={`stop-bill-result is-${r.tone} ${i === shown.length - 1 ? 'is-new' : ''}`}
              >
                <span className="stop-bill-op num">{r.op}</span>
                <div className="stop-bill-result-text">
                  <span className="stop-bill-expr num">{r.expr}</span>
                  <span className="stop-bill-note">{r.note}</span>
                </div>
                <span className="stop-bill-value num">{r.value}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {done && stop.closing && <p className="stop-bill-closing">{stop.closing}</p>}
    </Slide>
  );
}

/* ------------------------------------------------------------ Calc trace */
/* Precedence made watchable: the same expression reduced one operator at a
   time, with the part being evaluated lit up, at whatever pace the teacher
   talks. The steps already taken stay on screen as a stack so the class can
   see the whole chain, not just the current line. */
export function CalcTraceStop({ stop, substep = 0 }) {
  const at = Math.min(substep, stop.trace.length - 1);
  const frame = stop.trace[at];
  const history = stop.trace.slice(0, at);

  return (
    <Slide kicker={stop.kicker} title={stop.title} className="stop-trace">
      <div className="stop-trace-body">
        <div className="stop-trace-main">
          {history.length > 0 && (
            <div className="stop-trace-history">
              {/* Each step keeps the reason it happened. Without it the stack
                  is four crossed-out lines and the class has to remember why. */}
              {history.map((h, i) => (
                <span key={i} className="stop-trace-history-line">
                  <span className="num">{h.tokens.join(' ')}</span>
                  {h.note && <em>{h.note}</em>}
                </span>
              ))}
            </div>
          )}

          <div className={`stop-trace-line num ${frame.final ? 'is-final' : ''}`}>
            {frame.tokens.map((t, i) => (
              <span
                key={i}
                className={`stop-trace-token ${frame.focus.includes(i) ? 'is-focus' : ''}`}
              >
                {t}
              </span>
            ))}
          </div>

          <p className={`stop-trace-note ${frame.final ? 'is-final' : ''}`}>{frame.note}</p>
        </div>

        {stop.wrong && (
          <div className={`stop-trace-wrong ${at >= stop.trace.length - 1 ? '' : 'is-dim'}`}>
            <span className="stop-trace-wrong-icon"><DeckIcon name="flame" size={40} /></span>
            <div>
              <strong>{stop.wrong.head}</strong>
              <p>{stop.wrong.body}</p>
            </div>
            <span className="stop-trace-wrong-value num">{stop.wrong.value}</span>
          </div>
        )}
      </div>
    </Slide>
  );
}

/* -------------------------------------------------------------- Op table */
/**
 * The three operators this lesson exists for. A seven-row table gave all
 * seven the same weight and then shouted at three of them, so the eye had
 * nowhere to land; here the four the class already knows are a quiet strip
 * and the three new ones are the slide.
 *
 * Each of the three shows the same seven units grouped in twos, and colours
 * the part it is about: the whole split for `/`, the full groups for `//`,
 * the leftover for `%`. Same numbers, three different answers — which is the
 * thing being taught, said in a picture instead of a sentence.
 */
function UnitSplit({ total, per, mode }) {
  const groups = Math.floor(total / per);
  const left = total % per;
  return (
    <div className="stop-units" aria-hidden="true">
      {Array.from({ length: groups }).map((_, g) => (
        <span key={g} className={`stop-units-group ${mode === 'remainder' ? 'is-dim' : 'is-on'}`}>
          {Array.from({ length: per }).map((_, i) => (
            <i key={i} />
          ))}
        </span>
      ))}
      {left > 0 && (
        <span className={`stop-units-group is-part ${mode === 'split' ? 'is-split' : ''} ${mode === 'whole' ? 'is-dim' : 'is-on'}`}>
          {Array.from({ length: left }).map((_, i) => (
            <i key={i} />
          ))}
        </span>
      )}
    </div>
  );
}

export function OpTableStop({ stop, substep = 0 }) {
  const shown = stop.steps ? substep : stop.focus.length;
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="stop-optable">
      <div className="stop-known">
        <span className="stop-known-label">{stop.knownLabel}</span>
        <div className="stop-known-row">
          {stop.known.map((k) => (
            <span key={k.op} className="stop-known-chip">
              <b className="num">{k.op}</b>
              <span>{k.name}</span>
              <em className="num">{k.example} = {k.result}</em>
            </span>
          ))}
        </div>
      </div>

      <div className="stop-focus-row">
        {stop.focus.map((f, i) => (
          <div key={f.op} className={`stop-focus-card ${i < shown ? 'is-shown' : 'is-hidden'}`}>
            <div className="stop-focus-head">
              <b className="num">{f.op}</b>
              <span>{f.name}</span>
            </div>
            <UnitSplit total={stop.unitTotal} per={stop.unitPer} mode={f.viz} />
            <div className="stop-focus-sum num">
              {f.expr}
              <span className="stop-focus-eq">=</span>
              <strong>{f.value}</strong>
            </div>
            <p className="stop-focus-gloss">{f.gloss}</p>
          </div>
        ))}
      </div>

      {stop.closing && shown >= stop.focus.length && (
        <p className="stop-optable-closing">{stop.closing}</p>
      )}
    </Slide>
  );
}

/* ------------------------------------------------------------ Precedence */
export function PrecedenceStop({ stop }) {
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="stop-prec">
      <div className="stop-prec-grid">
        {stop.tiers.map((t) => (
          <div key={t.rank} className={`stop-prec-tier ${t.rank === 1 ? 'is-first' : ''}`}>
            <span className="stop-prec-rank num">{t.rank}</span>
            <span className="stop-prec-ops">
              {t.ops.map((op) => (
                <span key={op} className="stop-prec-op num">{op}</span>
              ))}
            </span>
            <span className="stop-prec-label">
              {t.label}
              {t.note && <em>{t.note}</em>}
            </span>
          </div>
        ))}
      </div>
      <div className="stop-prec-rules">
        {stop.rules.map((r, i) => (
          <div key={i} className="stop-prec-rule">
            <strong>{r.head}</strong>
            <span className="num">{r.body}</span>
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* ------------------------------------------------------------------ Step */
export function StepStop({ stop }) {
  if (stop.grid) {
    return (
      <Slide kicker={`ขั้นที่ ${stop.n}`} title={stop.title} className="stop-step stop-step-grid-slide">
        <div className="stop-rule-grid">
          {stop.points.map((p, i) => (
            <div key={i} className={`stop-rule-card ${p.warn ? 'is-warn' : ''}`}>
              <div className="stop-rule-card-head">
                <span className="stop-rule-icon"><DeckIcon name={p.icon} size={40} /></span>
                <span className="stop-rule-num num">{i + 1}</span>
              </div>
              <h3>{p.head}</h3>
              {p.body && <p>{p.body}</p>}
            </div>
          ))}
        </div>
      </Slide>
    );
  }

  return (
    <Slide
      kicker={`ขั้นที่ ${stop.n}`}
      title={stop.title}
      badge={stop.badge}
      badgeWarn={stop.warn}
      className="stop-step"
    >
      <div className="stop-step-body">
        <div className="stop-step-text">
          {stop.headline && <h2 className="stop-step-headline">{stop.headline}</h2>}
          {stop.lead && <p className="stop-step-lead">{stop.lead}</p>}
          {stop.points && (
            <ul className="stop-point-list">
              {stop.points.map((p, i) => (
                <li key={i} className={p.warn ? 'is-warn' : ''}>
                  <span className="stop-point-icon"><DeckIcon name={p.icon} size={48} /></span>
                  <div className="stop-point-content">
                    <strong>{p.head}</strong>
                    {p.body && <span className="stop-point-body">{p.body}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {stop.callout && (
            <div className="stop-callout">
              <strong>{stop.callout.head}</strong>
              <p>{stop.callout.body}</p>
            </div>
          )}
        </div>
        {stop.aside && <StepAside aside={stop.aside} />}
      </div>
    </Slide>
  );
}

function StepAside({ aside }) {
  if (aside.type === 'terminal') {
    return (
      <div className="stop-terminal">
        <div className="stop-terminal-bar">
          <span /><span /><span />
          <span className="stop-terminal-title">Terminal</span>
        </div>
        <pre>
          {aside.lines.map(([kind, text], i) => (
            <div key={i} className={`t-${kind}`}>
              {kind === 'in' && <span className="t-prompt">$ </span>}
              {text}
            </div>
          ))}
        </pre>
      </div>
    );
  }
  if (aside.type === 'image') {
    return (
      <figure className={`stop-shot frame-${aside.frame || 'plain'}`}>
        {aside.frame === 'browser' && (
          <div className="stop-shot-bar">
            <span className="stop-shot-dot" />
            <span className="stop-shot-dot" />
            <span className="stop-shot-dot" />
            {aside.url && <span className="stop-shot-url">{aside.url}</span>}
          </div>
        )}
        <img src={aside.src} alt={aside.url || ''} />
      </figure>
    );
  }
  if (aside.type === 'installer') {
    return (
      <div className="stop-aside stop-installer">
        <div className="stop-installer-bar">
          <span className="stop-installer-icon"><DeckIcon name="gear" size={22} /></span> Python 3.14.6 (64-bit) Setup
        </div>
        <div className="stop-installer-body">
          <label className="is-off"><span className="box" /> Use admin privileges when installing py.exe</label>
          <label className="is-on">
            <span className="box checked" aria-hidden="true">✓</span> Add python.exe to PATH
            <span className="stop-installer-flag">ติ๊กช่องนี้เสมอ!</span>
          </label>
          <label className="is-off"><span className="box" /> Install launcher for all users</label>
          <div className="stop-installer-actions">
            <span className="btn-fake ghost">Customize installation</span>
            <span className="btn-fake">Install Now</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

/* ------------------------------------------------------------ SelectType */
function PersonIcon({ size = 28, filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="6.5" r="3.5" />
      <path d="M4.5 21c0-4.4 3.36-7 7.5-7s7.5 2.6 7.5 7" />
    </svg>
  );
}

/** Ticks a number from 0 up to `target` over `durationMs`, restarting whenever `runId` changes. */
function useCountUp(target, durationMs, runId) {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    setValue(0);
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      setValue(t * target);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs, runId]);
  return value;
}

function CountDemo({ demo, runId }) {
  const count = Math.floor(useCountUp(demo.total, 1800, runId));
  return (
    <div className="stop-count-demo">
      <div className="stop-count-icons" key={runId}>
        {Array.from({ length: demo.total }, (_, i) => (
          <span
            key={i}
            className="stop-count-icon"
            style={{ animationDelay: `${120 + i * 300}ms` }}
          >
            <PersonIcon size={68} filled />
          </span>
        ))}
      </div>
      <div className="stop-count-readout">
        <span className="num">{count}</span>
        <span className="stop-count-unit">{demo.unit}</span>
      </div>
      <p className="stop-demo-caption">{demo.label}</p>
    </div>
  );
}

function MeasureDemo({ demo, runId }) {
  const value = useCountUp(demo.value, 1800, runId);
  const pct = Math.min(100, (value / 200) * 100); // 200 = the gauge's visual ceiling
  return (
    <div className="stop-measure-demo">
      <div className="stop-measure-figure">
        <PersonIcon size={150} />
        <div className="stop-measure-track">
          <div className="stop-measure-fill" style={{ height: `${pct}%` }} />
        </div>
      </div>
      <div className="stop-measure-readout">
        <span className="num">{value.toFixed(1)}</span>
        <span className="stop-count-unit">{demo.unit}</span>
      </div>
      <p className="stop-demo-caption">{demo.label}</p>
    </div>
  );
}

function SelectTypeColumn({ col }) {
  const [runId, setRunId] = React.useState(0);
  return (
    <div className="stop-select-col">
      <h3>{col.head}</h3>
      <p>{col.body}</p>
      <div className="stop-select-demo">
        {col.demo.type === 'count' && <CountDemo demo={col.demo} runId={runId} />}
        {col.demo.type === 'measure' && <MeasureDemo demo={col.demo} runId={runId} />}
      </div>
      <button type="button" className="stop-select-replay" onClick={() => setRunId((n) => n + 1)}>
        <RotateCcwIcon size={18} /> เล่นอีกครั้ง
      </button>
    </div>
  );
}

export function SelectTypeStop({ stop }) {
  return (
    <Slide title={stop.title} className="stop-select-type">
      <div className="stop-select-cols">
        {stop.columns.map((col, i) => (
          <SelectTypeColumn key={i} col={col} />
        ))}
      </div>
    </Slide>
  );
}

/* --------------------------------------------------------------- Divider */
export function DividerStop({ stop }) {
  return (
    <section className="stop stop-divider">
      <span className="stop-divider-part">{stop.part}</span>
      <h1>{stop.title}</h1>
    </section>
  );
}

/* -------------------------------------------------------------- Pipeline */
export function PipelineStop({ stop }) {
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="stop-pipeline-slide">
      {stop.note && (
        <div className="stop-pipe-note-row">
          <p className="stop-note"><DeckIcon name="bulb" size={26} /> {stop.note}</p>
        </div>
      )}
      <div className="stop-pipeline">
        {stop.stages.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`stop-pipe-card stage-${s.n}`}>
              <div className="stop-pipe-card-head">
                <span className="stop-pipe-badge num">{String(s.n).padStart(2, '0')}</span>
                <span className="stop-pipe-tag">ส่วนที่ {s.n}</span>
              </div>
              <div className="stop-pipe-icon">
                <DeckIcon name={s.icon} size={56} />
              </div>
              <h3>{s.label}</h3>
              <p>{s.body}</p>
              {s.role && <div className="stop-pipe-role">{s.role}</div>}
            </div>
            {i < stop.stages.length - 1 && (
              <div className="stop-pipe-arrow">
                <span>{stop.connectors[i]}</span>
                <svg width="48" height="18" viewBox="0 0 44 18" fill="none">
                  <path d="M0 9h37m0 0-7-7m7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </Slide>
  );
}

/* -------------------------------------------------------------- Glossary */
export function GlossaryStop({ stop }) {
  return (
    <Slide title={stop.title} className="stop-glossary-slide">
      <div className="stop-glossary">
        {stop.terms.map((t, i) => (
          <div key={i} className="stop-term">
            <span className="stop-term-icon"><DeckIcon name={t.icon} size={34} /></span>
            <span className="stop-term-text"><strong>{t.term}</strong> {t.def}</span>
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* ------------------------------------------------------------------ Quiz */
/** Seconds left of a question's time limit, from when it was opened. */
function useCountdown(limitSeconds, pushedAt, running) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [running]);
  const start = pushedAt || now;
  return Math.max(0, Math.ceil((limitSeconds || 0) - (now - start) / 1000));
}

/* Kahoot's clock: the seconds left on the left, the answers in on the right.
   The limit is the window in which a fast answer earns its speed bonus;
   answers still count after it, until the instructor reveals. */
function QuizTimer({ limitSeconds, pushedAt }) {
  const left = useCountdown(limitSeconds, pushedAt, true);
  return (
    <div className={`pq-timer ${left <= 5 ? 'is-low' : ''}`} aria-label={`เหลือ ${left} วินาที`}>
      <span className="num">{left}</span>
    </div>
  );
}

function QuizAnswers({ answered, total }) {
  return (
    <div className="pq-answers">
      <span className="pq-answers-num num">{answered ?? 0}</span>
      <span className="pq-answers-label">คำตอบ{total ? ` จาก ${total}` : ''}</span>
    </div>
  );
}

export function QuizStop({ stop, room, findQuestion, deck }) {
  const row = findQuestion(stop.order);
  const objective = getObjectiveForQuestion(room.roomCode, stop.order);
  const isLive = room.question && room.question.orderIndex === stop.order;

  let question;
  let phase;
  if (isLive) {
    question = room.question;
    phase = room.question.revealed ? 'revealed' : 'open';
  } else if (row) {
    question = { id: row.id, type: row.type, payload: row.payload_json, tally: null, revealed: false, answeredCount: 0 };
    phase = 'preview';
  } else {
    return <section className="stop stop-pad"><h1 className="stop-title">ไม่พบคำถามข้อนี้</h1></section>;
  }

  const answered = room.question?.answeredCount;
  const total = room.question?.totalPlayers ?? room.connectedCount;
  return (
    <section className="stop stop-quiz">
      <ProjectorQuestion
        question={question}
        objective={objective}
        phase={phase}
        answered={answered}
        total={total}
        clock={deck?.meta?.theme === 'tag' && phase === 'open' ? {
          left: <QuizTimer limitSeconds={question.timeLimitSeconds} pushedAt={question.pushedAt} />,
          right: <QuizAnswers answered={answered} total={total} />,
        } : null}
      />
    </section>
  );
}

/* --------------------------------------------------------------- Results */
export function ResultsStop({ room, preview }) {
  return (
    <section className="stop stop-results">
      <Leaderboard
        leaderboard={room.leaderboard}
        isTeacher
        onAwardStar={room.actions.awardStar}
        celebrate={!preview}
      />
    </section>
  );
}

/* ----------------------------------------------------------------- Outro */
export function OutroStop({ stop }) {
  return (
    <section className="stop stop-divider stop-outro">
      <h1>{stop.title}</h1>
      {stop.body && <p>{stop.body}</p>}
      {stop.credit && <span className="stop-outro-credit">{stop.credit}</span>}
    </section>
  );
}

/* ------------------------------------------------------------- Hand Demo */
export function HandDemoStop({ stop, preview }) {
  // The control panel's miniature must not switch the webcam on.
  if (preview) {
    return (
      <section className="stop stop-hand-demo stop-pad">
        <div className="stop-preview-stub">
          <DeckIcon name="monitor" size={44} />
          <p>{stop.title || 'กล้องตรวจจับมือ'}</p>
          <span>แสดงบนจอฉายเท่านั้น</span>
        </div>
      </section>
    );
  }
  return (
    <section className="stop stop-hand-demo stop-pad">
      <HandTrackingCam />
    </section>
  );
}

